import {computed, Injectable, OnDestroy, signal, WritableSignal} from '@angular/core';
import {filter, takeUntil} from 'rxjs/operators';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {Subject} from 'rxjs';


enum Breakpoint {
  XL = 'xl',
  LG = 'lg',
  MD = 'md',
  SM = 'sm',
  XS = 'xs'
}

@Injectable({
  providedIn: 'root'
})
export class BreakpointService implements OnDestroy{

  public currentBreakpoint: WritableSignal<Breakpoint> = signal(Breakpoint.XL);

  public isXl = computed(() => {
    return this.currentBreakpoint() === Breakpoint.XL;
  });

  public isLg = computed(() => {
    return this.currentBreakpoint() === Breakpoint.LG;
  });

  public isMd = computed(() => {
    return this.currentBreakpoint() === Breakpoint.MD;
  });

  public isSm = computed(() => {
    return this.currentBreakpoint() === Breakpoint.SM;
  });

  public isXs = computed(() => {
    return this.currentBreakpoint() === Breakpoint.XS;
  });

  public isMobile = computed(() => {
    return !this.isXl() && !this.isLg();
  });

  /**
   * Bootstrap Breakpoints in pixels
   */
  private readonly breakpoints: Map<Breakpoint, number> = new Map<Breakpoint, number>([
    [Breakpoint.XL, 1200],
    [Breakpoint.LG, 992],
    [Breakpoint.MD, 768],
    [Breakpoint.SM, 576]
  ]);

  private readonly breakpointMediaQueries: Map<Breakpoint, string> = new Map<Breakpoint, string>([
    [Breakpoint.XL, '(min-width: '+ this.breakpoints.get(Breakpoint.XL) + 'px)'],
    [Breakpoint.LG, '(min-width: ' + this.breakpoints.get(Breakpoint.LG) + 'px) ' +
    'and (max-width: ' + (this.breakpoints.get(Breakpoint.XL) -1) + 'px)'],
    [Breakpoint.MD, '(min-width: ' + this.breakpoints.get(Breakpoint.MD) + 'px) ' +
    'and (max-width: ' + (this.breakpoints.get(Breakpoint.LG) -1) + 'px)'],
    [Breakpoint.SM, '(min-width: ' + this.breakpoints.get(Breakpoint.SM) + 'px) ' +
    'and (max-width: ' + (this.breakpoints.get(Breakpoint.MD) -1) + 'px)'],
    [Breakpoint.XS, '(max-width: '+ this.breakpoints.get(Breakpoint.SM) + 'px)']
  ]);

  private readonly unsubscribe: Subject<void> = new Subject<void>();

  constructor(private readonly breakpointObserver: BreakpointObserver) {
    this.breakpointMediaQueries.forEach((observer, breakpoint) => {
      this.breakpointObserver
        .observe([observer])
        .pipe(takeUntil(this.unsubscribe), filter(state => !!state))
        .subscribe((state: BreakpointState) => {
          if (state.matches) {
            this.currentBreakpoint.set(breakpoint);
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
